import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filterUndefined } from 'src/app/lego-utils';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { asyncScheduler, combineLatest, Subscription } from 'rxjs';
import { debounceTime, map, observeOn } from 'rxjs/operators';
import { Area } from 'src/app/models';
import { DesignService, IDesignQuery, ValidationService } from 'src/app/state';
import { AnalyticsService } from '../../../services';

@Component({
  selector: 'em-design-lookup',
  styleUrls: ['./design-lookup.component.less'],
  templateUrl: './design-lookup.component.html',
  standalone: false,
})
export class DesignLookupComponent implements OnInit, OnDestroy {
  public readonly design$ = this.designService.design$;
  public readonly isLoading$ = this.designService.loading$;
  public readonly error$ = this.designService.error$;
  public readonly isDesignLoading$ = this.designService.loading$;

  public readonly specificationValidationFails$ = this.validationService.selectHasViolationsInPath(
    Area.Specifications
  );

  public readonly materialsValidationFails$ = this.validationService.selectHasViolationsInPath(
    Area.Materials
  );

  private readonly subscriptions = new Subscription();

  public activeSubTab = 'Element Design';

  public constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly designService: DesignService,
    private readonly validationService: ValidationService,
    private readonly notification: NzNotificationService,
    private readonly analyticsService: AnalyticsService
  ) {}

  public ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([this.activatedRoute.paramMap, this.activatedRoute.queryParamMap])
        .pipe(
          // paramMap & queryParamMap emit at different times for the same navigation
          // and queryParamMap doesn't emit again if it's unchanged even though it's a new navigation
          //
          // debouncing fixes this "double emit" by waiting for the params it to be stabilized
          debounceTime(0),
          map(([paramMap, queryParamMap]): IDesignQuery => {
            const designId = paramMap.has('designId')
              ? (paramMap.get('designId') as string)
              : undefined;

            const equipmentNumber = queryParamMap.has('equipmentNumber')
              ? (queryParamMap.get('equipmentNumber') as string)
              : undefined;

            return { designId, equipmentNumber };
          }),
          // this ensures that designStore is cleared sync if no designId is provided
          filterUndefined(() => {
            this.designService.setQuery(undefined);
          }),
          // setQuery needs to be an async operation to remove the ExpressionChangedAfterItHasBeenChecked error
          observeOn(asyncScheduler)
        )
        .subscribe((query) => {
          this.designService.setQuery(query);
        })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public set40C(designId: string) {
    if (this.designService.getDesign()?.qa40Check) {
      this.notification.success('40C already set', '40C has already been set successfully');

      return;
    }

    this.subscriptions.add(
      this.designService.set40C(designId).subscribe({
        next: () => {
          this.notification.success('40C set', '40C was successfully set');
        },
        error: (error: unknown) => {
          const sapErrorMessage =
            error instanceof Error ? error.message.split('§§§')[0] : 'Unknown error';

          this.notification.error('Error', sapErrorMessage, { nzDuration: 0 });
        },
      })
    );
  }

  public adobeTrackingTabChangeWithSubTab(subTab: string): void {
    this.activeSubTab = subTab;

    this.analyticsService.location = {
      ...this.analyticsService.dataLayer.location,
      subTab,
    };
  }
}
